import React, { useContext, useEffect } from 'react';
import { BdsTypo } from 'blip-ds/dist/blip-ds-react';
import AdvancedDailyEngagedUsersContext from '../../../../contexts/Billing/AdvancedDailyEngagedUsersContext';
import { BlipLoadingComponent } from '../../../../components/BlipLoading/BlipLoadingComponent';
import { PageHeaderComponent } from '../../components/Metrics/PageHeader/PageHeaderComponent';
import { origin, REPORT_PERIOD_SELECT } from '../../../../shared/SegmentEvents';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { useAdvancedDeuConsumptionContext } from '../../../../contexts/Billing/AdvancedDeuConsumptionContext';
import AdvancedDailyEngagedUserTitle from './AdvancedDailyEngagedUserTitle';
import { useDeuAiEnabled } from './helpers';

import {
  CompiledAdvancedDailyEngagedUserPaper,
  MetricsGrid,
  MetricCardItem,
  TitleContainer,
  Divider,
} from '../styles';

const CompiledAdvancedDailyEngagedUsers: React.FC = () => {
  const {
    content,
    dateToSearch,
    periodOptions,
    setDateToSearch,
    tenant,
    loggedUser,
    trackingProps,
    getPlanStatus,
    isMetricAllowed,
  } = useContext(AdvancedDailyEngagedUsersContext);

  const isDeuAiEnabled = useDeuAiEnabled();

  const {
    compiledAdvancedDailyEngagedUserDataState,
    consolidatedAdvancedDeuApiRequisition,
    isAdvancedDeuLoading,
  } = useAdvancedDeuConsumptionContext();

  const { compiledTotalAdvancedDailyEngagedUserConsumption } =
    compiledAdvancedDailyEngagedUserDataState;

  useEffect(() => {
    consolidatedAdvancedDeuApiRequisition(dateToSearch, (metrics) =>
      metrics.some((m) => isMetricAllowed(m))
    );
  }, [dateToSearch, isMetricAllowed, consolidatedAdvancedDeuApiRequisition]);

  const periodOptionChangeEvent = async (selectedOption: any) => {
    const dateToRetrieveData = new Date(selectedOption.detail.value);
    setDateToSearch(dateToRetrieveData);

    const planStatus = await getPlanStatus();

    trackEvent(REPORT_PERIOD_SELECT, tenant, loggedUser, {
      ...trackingProps,
      origin: origin.ADVANCEDDEU,
      date: dateToRetrieveData,
      planStatus,
    });
  };

  const deu =
    compiledTotalAdvancedDailyEngagedUserConsumption.dailyEngagedUsersTotal || 0;

  const userConv =
    compiledTotalAdvancedDailyEngagedUserConsumption.userConversationTotal || 0;

  const prospectConv =
    compiledTotalAdvancedDailyEngagedUserConsumption.prospectConversationTotal || 0;

  const activeMsg =
    compiledTotalAdvancedDailyEngagedUserConsumption.activeMessageTotal || 0;

  const dailyEngagedUserAI =
    compiledTotalAdvancedDailyEngagedUserConsumption.dailyEngagedUserAITotal || 0;

  return (
    <>
      <PageHeaderComponent
        title={content.title}
        dateToSearch={dateToSearch}
        periodOptions={periodOptions}
        periodOptionChangeEvent={periodOptionChangeEvent}
      />

      <CompiledAdvancedDailyEngagedUserPaper>
        <TitleContainer>
          <AdvancedDailyEngagedUserTitle />
        </TitleContainer>

        <Divider />

       {isAdvancedDeuLoading ? (
  <BlipLoadingComponent />
) : (
  <MetricsGrid>
    <MetricCardItem>
      <BdsTypo
        variant="fs-16"
        bold="bold"
        margin={false}
        style={{ marginBottom: '8px' }}
      >
        {content.compiledInformation.labels.deus}
      </BdsTypo>

      <BdsTypo variant="fs-32" bold="bold" margin={false}>
        {deu.toLocaleString(content.locale)}
      </BdsTypo>
    </MetricCardItem>

    <MetricCardItem>
      <BdsTypo
        variant="fs-16"
        bold="bold"
        margin={false}
        style={{ marginBottom: '8px' }}
      >
        {content.compiledInformation.labels.prospectConversations}
      </BdsTypo>

      <BdsTypo variant="fs-32" bold="bold" margin={false}>
        {prospectConv.toLocaleString(content.locale)}
      </BdsTypo>
    </MetricCardItem>

    <MetricCardItem>
      <BdsTypo
        variant="fs-16"
        bold="bold"
        margin={false}
        style={{ marginBottom: '8px' }}
      >
        {isDeuAiEnabled
          ? content.compiledInformation.labels.aiAdditional
          : content.compiledInformation.labels.userConversations}
      </BdsTypo>

      <BdsTypo variant="fs-32" bold="bold" margin={false}>
        {isDeuAiEnabled
          ? dailyEngagedUserAI.toLocaleString(content.locale)
          : userConv.toLocaleString(content.locale)}
      </BdsTypo>
    </MetricCardItem>

    <MetricCardItem>
      <BdsTypo
        variant="fs-16"
        bold="bold"
        margin={false}
        style={{ marginBottom: '8px' }}
      >
        {content.compiledInformation.labels.activeMessages}
      </BdsTypo>

      <BdsTypo variant="fs-32" bold="bold" margin={false}>
        {activeMsg.toLocaleString(content.locale)}
      </BdsTypo>
    </MetricCardItem>
  </MetricsGrid>
)}
      </CompiledAdvancedDailyEngagedUserPaper>
    </>
  );
};

export default CompiledAdvancedDailyEngagedUsers;